import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Col, Input, Loader, Message, Panel, Row, SelectPicker, Stack, Tag, toaster } from "rsuite";
import AdminDashboard from "../../components/AdminDashboard";
import PageBase from "../../components/PageBase";
import Header from "../../layouts/Header";
import { CommissionPayment } from "../../my-type";
import { authUserData } from "../../service";
import {
  getCommissionPaymentByOrderID,
  getCommissionPaymentReceiptImage,
  updateCommissionPayment,
  uploadCommissionPaymentReceipt,
} from "../../service/CommissionPaymentService";
import { hasRole } from "../../util/roles";

const statusOptions = [
  { label: "待轉帳", value: "pending" },
  { label: "已完成", value: "paid" },
];

const statusTagColorMap: Record<string, "orange" | "green" | "red" | "violet" | "blue"> = {
  pending: "orange",
  paid: "green",
  refund: "violet",
  cancelled: "red",
};

const statusLabelMap: Record<string, string> = {
  pending: "待轉帳",
  paid: "已完成",
  refund: "已退還",
  cancelled: "已取消",
};

const formatDateTime = (value: Date | string | null | undefined) => {
  if (!value) {
    return "-";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CommissionPaymentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const authUser = authUserData();
  const [record, setRecord] = useState<CommissionPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadRecord = async () => {
      if (!orderId) {
        setErrorMessage("缺少訂單編號。");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      const data = await getCommissionPaymentByOrderID(orderId);
      if (!data) {
        setErrorMessage("查無佣金付款資料。");
        setLoading(false);
        return;
      }

      setRecord(data);
      setStatus(data.status);
      setPaymentReference(data.paymentReference ?? "");
      setLoading(false);
    };

    loadRecord();
  }, [orderId]);

  useEffect(() => {
    let isActive = true;
    let objectUrl: string | null = null;

    const loadReceiptImage = async () => {
      if (!record?.transferReceiptImg) {
        setReceiptImageUrl(null);
        return;
      }

      const imageUrl = await getCommissionPaymentReceiptImage(record.orderID);
      if (!isActive) {
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }
        return;
      }

      objectUrl = imageUrl;
      setReceiptImageUrl(imageUrl);
    };

    loadReceiptImage();

    return () => {
      isActive = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [record?.orderID, record?.transferReceiptImg]);

  const isPayer = !!record && authUser?.id === record.payer;
  const isLeadPayee =
    !!record && authUser?.id === record.payee && hasRole(authUser?.roleString, "Lead");
  const canEditPendingFields = isPayer && status === "pending";
  const canMarkCompleted = !!record && status === "pending" && (isPayer || isLeadPayee);

  const handleSave = async () => {
    if (!record || !canMarkCompleted) {
      return;
    }

    setSaving(true);
    try {
      const updated = await updateCommissionPayment(record.orderID, {
        status: "paid",
        paymentReference: paymentReference.trim() || null,
      });
      setRecord(updated);
      setStatus(updated.status);
      setPaymentReference(updated.paymentReference ?? "");
      toaster.push(
        <Message showIcon type="success" closable>
          已將 {updated.orderID} 的佣金付款標記為已完成
        </Message>,
        { placement: "topEnd" }
      );
    } catch (error) {
      toaster.push(
        <Message showIcon type="error" closable>
          {error instanceof Error ? error.message : "更新佣金付款資料失敗"}
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUploadReceipt = async (file: File | null) => {
    if (!file || !record) {
      return;
    }

    setUploading(true);
    try {
      const result = await uploadCommissionPaymentReceipt(record.orderID, file);
      setRecord({
        ...record,
        transferReceiptImg: result.fileName,
      });
      toaster.push(
        <Message showIcon type="success" closable>
          已上傳 {record.orderID} 的轉帳證明
        </Message>,
        { placement: "topEnd" }
      );
    } catch (error) {
      toaster.push(
        <Message showIcon type="error" closable>
          {error instanceof Error ? error.message : "上傳轉帳證明失敗"}
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setUploading(false);
    }
  };

  const header = (
    <Header
      HeaderTitle="佣金付款明細"
      isShowRefresh={false}
      OnReturn={() => navigate("/commission-payments")}
      OnRefresh={() => { }}
    />
  );

  return (
    <AdminDashboard title="佣金付款明細">
      <PageBase
        header={header}
        content={
          loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Loader size="md" content="載入佣金付款資料中..." />
            </div>
          ) : errorMessage ? (
            <Message type="error" showIcon>{errorMessage}</Message>
          ) : !record ? (
            <Message type="info" showIcon>查無佣金付款資料</Message>
          ) : (
            <div style={{ textAlign: "left" }}>
              <Panel bordered header="付款摘要" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Link to={`/orders/${record.orderID}`}>前往訂單 {record.orderID}</Link>
                </div>
                <Stack spacing={8} wrap>
                  <Tag color={statusTagColorMap[record.status] ?? "blue"}>
                    {statusLabelMap[record.status] ?? record.status}
                  </Tag>
                  <strong className="fleet-payment-amount">NT$ {record.amount.toLocaleString('zh-TW')}</strong>
                </Stack>
              </Panel>

              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Panel bordered header="付款資訊" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>收款人</strong><div>{record.payeeName || "-"}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>付款人</strong><div>{record.payerName || "-"}</div></div>
                      </Col>
                      <Col xs={24}>
                        <div style={{ marginBottom: 14 }}><strong>付款說明</strong><div>{record.paymentDescription || "-"}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>付款備註</strong><div>{record.paymentReference || "-"}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>狀態</strong><div>{statusLabelMap[record.status] ?? record.status}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>收款帳號</strong><div>{record.payeeBankFullAccount || "-"}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>付款帳號</strong><div>{record.payerBankFullAccount || "-"}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>建立時間</strong><div>{formatDateTime(record.createdAt)}</div></div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 14 }}><strong>付款時間</strong><div>{formatDateTime(record.payAt)}</div></div>
                      </Col>
                      <Col xs={24}>
                        <div><strong>更新時間</strong><div>{formatDateTime(record.updatedAt)}</div></div>
                      </Col>
                    </Row>
                  </Panel>
                </Col>

                <Col xs={24} md={10}>
                  <Panel bordered header="轉帳證明" style={{ marginBottom: 16 }}>
                    {receiptImageUrl ? (
                      <a href={receiptImageUrl} target="_blank" rel="noreferrer">
                        <img
                          src={receiptImageUrl}
                          alt="轉帳證明"
                          style={{
                            width: "100%",
                            maxHeight: 360,
                            objectFit: "contain",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            background: "#f8fafc",
                          }}
                        />
                      </a>
                    ) : (
                      <div style={{ color: "#64748b" }}>尚未上傳轉帳證明</div>
                    )}
                  </Panel>

                  <Panel bordered header="維護資料">
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 6, fontWeight: 600 }}>狀態</div>
                      <SelectPicker
                        searchable={false}
                        cleanable={false}
                        data={statusOptions}
                        value={status}
                        onChange={(value) => setStatus(value ?? record.status)}
                        style={{ width: "100%" }}
                        disabled
                      />
                    </div>

                    {canEditPendingFields && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 6, fontWeight: 600 }}>付款備註</div>
                        <Input
                          value={paymentReference}
                          onChange={setPaymentReference}
                          placeholder="請輸入付款備註"
                        />
                      </div>
                    )}

                    {canEditPendingFields && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ marginBottom: 6, fontWeight: 600 }}>上傳轉帳證明</div>
                        <Stack spacing={12} alignItems="center" wrap>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={uploading}
                            onChange={(event) => {
                              const file = event.target.files?.[0] ?? null;
                              void handleUploadReceipt(file);
                              event.currentTarget.value = "";
                            }}
                          />
                          {uploading && <span style={{ color: "#64748b" }}>上傳中...</span>}
                        </Stack>
                      </div>
                    )}

                    {!canMarkCompleted ? (
                      <Message type="info" showIcon style={{ marginBottom: 16 }}>
                        僅付款人，或身為收款人的代理隊長，可在待轉帳狀態下將此筆佣金付款標記為已完成。
                      </Message>
                    ) : isLeadPayee && !isPayer ? (
                      <Message type="info" showIcon style={{ marginBottom: 16 }}>
                        若司機已完成轉帳但尚未更新狀態，您可直接將此筆佣金付款標記為已完成。
                      </Message>
                    ) : null}

                    {isPayer && (
                      <Message type="info" showIcon style={{ marginBottom: 16 }}>
                        付款人可補充備註、上傳轉帳證明，並將此筆佣金付款標記為已完成。
                      </Message>
                    )}

                    <Button appearance="primary" loading={saving} onClick={handleSave} disabled={!canMarkCompleted}>
                      {isLeadPayee && !isPayer ? "標記為已完成" : "儲存並標記完成"}
                    </Button>
                  </Panel>
                </Col>
              </Row>
            </div>
          )
        }
        showSide={false}
      />
    </AdminDashboard>
  );
};

export default CommissionPaymentDetail;
